import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton } from '@mui/x-data-grid';
import { apiService } from '../services/apiService';
import { downloadResult, errorMessage } from '../services/quoteUtils';
import { combineBOMs, searchCombined } from '../services/workspaceUtils';
import { ExpandedRowContext, ExpandableBOMRow, EXPANDED_ROW_HEIGHT } from './ExpandableBOMRow';
import QuoteComparison from './QuoteComparison';

function Toolbar() {
  return <GridToolbarContainer><GridToolbarColumnsButton /></GridToolbarContainer>;
}

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [boms, setBoms] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [dialog, setDialog] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [available, setAvailable] = useState([]);
  const [attachId, setAttachId] = useState('');
  const [filteredExport, setFilteredExport] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const generation = useRef(0);
  const load = useCallback(async () => {
    const version = ++generation.current;
    const result = await apiService.getWorkspace(projectId);
    const details = [];
    // Bound concurrent large BOM responses; do not request raw/unfiltered objects.
    for (let i = 0; i < result.boms.length; i += 4) {
      const batch = await Promise.all(result.boms.slice(i, i + 4).filter(bom => bom.status === 'ready')
        .map(bom => apiService.getBOM(bom.bom_id)));
      details.push(...batch.filter(bom => bom.project?.project_id === projectId));
    }
    if (version !== generation.current) return;
    setProject(result); setBoms(details);
  }, [projectId]);
  const reload = async () => {
    setBusy(true); setError('');
    try { await load(); }
    catch (failure) { setProject(null); setBoms([]); setError(errorMessage(failure)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    setProject(null); setBoms([]); setError(''); setSearch(''); setExpanded(null); setDialog('');
    load().catch(failure => setError(errorMessage(failure)));
    return () => { generation.current += 1; };
  }, [load]);
  const combined = useMemo(() => combineBOMs(boms), [boms]);
  const rows = useMemo(() => searchCombined(combined.rows, search), [combined.rows, search]);
  const getRowHeight = useCallback(({ id }) => id === expanded ? EXPANDED_ROW_HEIGHT : 34, [expanded]);
  const mutate = async (operation, values) => {
    if (busy) return false;
    setBusy(true); setError('');
    let saved = false;
    try {
      await apiService.mutateWorkspace(operation, {
        ...values, project_id: projectId, revision: project.revision
      });
      saved = true;
      await load();
      return true;
    } catch (failure) {
      setError(`${saved ? 'Project change saved, but refreshing failed. ' : ''}${errorMessage(failure)} Reload before retrying; form values are kept.`);
      return false;
    } finally { setBusy(false); }
  };
  const download = async format => {
    setBusy(true); setError('');
    try {
      downloadResult(await apiService.exportWorkspace({
        project_id: projectId, format,
        columns: combined.names.filter(name => columnVisibility[`column:${name}`] !== false),
        ...(filteredExport ? { rows: rows.map(row => ({ bom_id: row.bom_id, line_item_id: row.line_item_id })) } : {})
      }));
    } catch (failure) { setError(errorMessage(failure)); }
    finally { setBusy(false); }
  };
  if (!project) return <div>{error ? <Alert variant="danger">{error}<Button onClick={reload}>Retry project</Button></Alert>
    : <Spinner role="status" animation="border"><span className="visually-hidden">Loading project</span></Spinner>}</div>;
  const columns = [
    { field: '__line', headerName: '#', width: 92, sortable: false, filterable: false, hideable: false,
      renderCell: ({ row }) => <div className="d-flex gap-1 align-items-center">
        <button className="bomwise-expand-toggle" aria-label={`${expanded === row.id ? 'Collapse' : 'Expand'} quotes for ${row.source_bom} line ${row.order_index + 1}`}
          aria-expanded={expanded === row.id} disabled={busy || !row.item.quote_access}
          onClick={() => setExpanded(previous => previous === row.id ? null : row.id)}>{expanded === row.id ? '▾' : '▸'}</button>{row.order_index + 1}
      </div> },
    { field: 'source_bom', headerName: 'Source BOM', width: 210, hideable: false, filterable: false,
      renderCell: ({ row }) => <Link to={`/bom/${encodeURIComponent(row.bom_id)}`}>{row.source_bom}</Link> },
    ...combined.names.map(column => ({
      field: `column:${column}`, headerName: column, width: /description|notes/i.test(column) ? 280 : 160, filterable: false,
      valueGetter: (_, row) => row.data[column] ?? ''
    }))
  ];
  return <div className="bomwise-workspace">
    <nav className="bomwise-breadcrumb" aria-label="Breadcrumb"><Link to="/projects">Projects</Link><span>/</span><span>{project.name}</span></nav>
    <div className="bomwise-page-heading">
      <div><div className="bomwise-eyebrow">PROJECT WORKSPACE</div><h1>{project.name}</h1>
        <p className="text-muted mb-0">{project.description || 'One project. Every BOM and supplier response, together.'}</p></div>
      <div className="d-flex flex-wrap gap-2">
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={reload}>Reload project</Button>
        {project.is_owner && <>
          <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => {
            setName(project.name); setDescription(project.description); setDialog('details');
          }}>Edit project</Button>
          <Button size="sm" disabled={busy} onClick={() => setDialog('share')}>Share project</Button>
        </>}
      </div>
    </div>
    {error && <Alert variant="danger">{error}</Alert>}
    <div className="bomwise-upload-strip">
      <div><strong>Keep related BOMs in one project</strong><span>Each upload keeps its own rows, columns, and vendor permissions.</span></div>
      {['owner', 'editor'].includes(project.role) && <Button as={Link} size="sm" to={`/upload?project=${encodeURIComponent(projectId)}`}>Upload BOM</Button>}
      {project.is_owner && <Button size="sm" variant="outline-primary" disabled={busy} onClick={async () => {
        setBusy(true); setError('');
        try {
          const result = await apiService.listProjects();
          setAvailable(result.projects.filter(bom => bom.is_owner && !project.boms.some(item => item.bom_id === bom.bom_id)));
          setAttachId(''); setDialog('attach');
        } catch (failure) { setError(errorMessage(failure)); }
        finally { setBusy(false); }
      }}>Add existing BOM</Button>}
    </div>
    <section className="bomwise-worksheet" aria-label="Project bill of materials">
      <div className="bomwise-workspace-stats">
        <div><span>BOMs</span><strong>{project.boms.length}</strong></div>
        <div><span>Loaded lines</span><strong>{combined.rows.length}</strong></div>
        <div><span>Project access</span><Badge bg="secondary" text="white">{project.role}</Badge></div>
      </div>
      <nav className="bomwise-bom-tabs" aria-label="Project BOMs">
        <span className="active">All BOMs</span>
        {project.boms.map(bom => <Link key={bom.bom_id} to={`/bom/${encodeURIComponent(bom.bom_id)}`}>
          {bom.name} <small>{bom.status !== 'ready' ? bom.status : bom.role}</small>
        </Link>)}
      </nav>
      <div className="bomwise-worksheet-toolbar">
        <strong>All parts <span className="bomwise-count">{rows.length}</span></strong>
        <Form.Control className="bomwise-worksheet-search" size="sm" aria-label="Search combined BOMs"
          placeholder="Search source BOMs and parts…" value={search} onChange={event => setSearch(event.target.value)} />
        <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => download('csv')}>Export CSV</Button>
        <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => download('xlsx')}>Export Excel</Button>
        <Form.Check label="Only searched rows" checked={filteredExport} onChange={event => setFilteredExport(event.target.checked)} />
      </div>
      <div className="bomwise-edit-bar"><span>Select a BOM tab to edit or manage vendor sharing. Expand a line to compare quotes; source lines are never merged.</span></div>
      <div className="bomwise-grid-container">
        <ExpandedRowContext.Provider value={{ expanded, renderDetail: row =>
          <QuoteComparison bom={row.bom} row={row.item} locked={busy} onChanged={load} onMutating={setBusy} /> }}>
          <DataGrid rows={rows} columns={columns} density="compact" rowHeight={34} columnHeaderHeight={36}
            columnVisibilityModel={columnVisibility} onColumnVisibilityModelChange={setColumnVisibility}
            getRowHeight={getRowHeight} disableRowSelectionOnClick loading={busy}
            onRowClick={({ row }, event) => {
              if (busy || !row.item.quote_access || event.target.closest('button,a,input,select')) return;
              setExpanded(previous => previous === row.id ? null : row.id);
            }}
            pageSizeOptions={[25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            slots={{ toolbar: Toolbar, row: ExpandableBOMRow }}
            localeText={{ noRowsLabel: project.boms.length ? 'No ready BOM lines match this search' : 'Upload or add a BOM to this project' }} />
        </ExpandedRowContext.Provider>
      </div>
      <div className="bomwise-worksheet-footer"><span>{rows.length} of {combined.rows.length} loaded source lines</span><span>Exports include visible data columns and source identities. Select a BOM to edit cells.</span></div>
    </section>
    <Modal show={!!dialog} onHide={() => !busy && setDialog('')} size={dialog === 'share' ? 'lg' : undefined}>
      <Modal.Header closeButton><Modal.Title>{dialog === 'share' ? `Share ${project.name}` : dialog === 'attach' ? 'Add an existing BOM' : 'Edit project'}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <fieldset disabled={busy}>
          {dialog === 'details' && <Form onSubmit={async event => {
            event.preventDefault(); if (await mutate('details', { name: name.trim(), description: description.trim() })) setDialog('');
          }}>
            <Form.Group className="mb-3" controlId="project-edit-name"><Form.Label>Project name</Form.Label>
              <Form.Control required maxLength={160} value={name} onChange={event => setName(event.target.value)} /></Form.Group>
            <Form.Group className="mb-3" controlId="project-edit-description"><Form.Label>Description</Form.Label>
              <Form.Control as="textarea" maxLength={2000} value={description} onChange={event => setDescription(event.target.value)} /></Form.Group>
            <Button type="submit" disabled={!name.trim()}>Save project</Button>
          </Form>}
          {dialog === 'share' && <>
            <p>Colleagues inherit access to current and future project BOMs. Vendors and custom scopes are configured with Share on each individual BOM.</p>
            <Alert variant="warning">Explicit BOM grants take precedence and remain independent. Revoking project access does not revoke a separate BOM grant.</Alert>
            <Form onSubmit={async event => { event.preventDefault(); if (await mutate('grant', { user_email: email, role })) setEmail(''); }}>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Form.Control type="email" required aria-label="Project recipient email" placeholder="colleague@example.com"
                  value={email} onChange={event => setEmail(event.target.value)} />
                <Form.Select aria-label="Project recipient role" value={role} onChange={event => setRole(event.target.value)}>
                  <option value="viewer">Read only</option><option value="editor">Full editing access</option>
                </Form.Select><Button type="submit">Grant project access</Button>
              </div>
            </Form>
            <Form.Control aria-label="Project share link" readOnly value={`${window.location.origin}/projects/${encodeURIComponent(projectId)}`} />
            <p className="text-muted mt-2">Copy and send this link yourself. Recipients must sign in with a verified matching email. No email is sent.</p>
            <Table size="sm" responsive><thead><tr><th>Email</th><th>Role</th><th>Access</th></tr></thead>
              <tbody>{project.permissions?.map(grant => <tr key={grant.user_id}>
                <td>{grant.user_email}</td><td>{grant.role}</td><td>
                  <Button size="sm" variant="outline-danger" onClick={() => mutate('revoke', { user_email: grant.user_email })}>Revoke project access</Button>
                </td>
              </tr>)}</tbody></Table>
          </>}
          {dialog === 'attach' && <>
            <p>Choose a BOM you own. Only ready or failed imports can be moved.</p>
            <Alert variant="warning">Project colleagues will gain inherited access. Moving a BOM from another project removes that project's inherited access; individual BOM grants remain.</Alert>
            <Form.Select aria-label="Existing BOM" value={attachId} onChange={event => setAttachId(event.target.value)}>
              <option value="">Choose a BOM…</option>
              {available.map(bom => <option key={bom.bom_id} value={bom.bom_id} disabled={!['ready', 'failed'].includes(bom.status)}>{bom.name}</option>)}
            </Form.Select>
            <Button className="mt-3" disabled={!attachId} onClick={async () => {
              try {
                const bom = await apiService.getBOM(attachId);
                if (await mutate('attach', { bom_id: attachId, bom_revision: bom.revision })) setDialog('');
              } catch (failure) { setError(errorMessage(failure)); }
            }}>Add BOM to project</Button>
          </>}
        </fieldset>
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" disabled={busy} onClick={reload}>Reload project (keep form)</Button>
        <Button variant="secondary" disabled={busy} onClick={() => setDialog('')}>Close</Button></Modal.Footer>
    </Modal>
  </div>;
}
